/**
 * Auth calls that must NOT ride the realtime hub.
 *
 * V3 A0-6: session restore used to be `RoomHub.ReconnectUser(userId)` invoked over an
 * *anonymous* SignalR connection — it took a bare user id, checked no credential, and returned a
 * freshly minted JWT for whoever was named. Anyone who could open a socket could ask for
 * `"admin"`. The hub method still exists (it also heals SignalR group membership after a
 * transport reconnect) but it now requires an authenticated connection, so the token has to come
 * from somewhere else first — this module.
 *
 * `POST /auth/refresh` exchanges a token this server previously issued for a fresh one. The old
 * token's signature, issuer and audience are validated normally; only the expiry is relaxed, so a
 * user who left a tab closed overnight is not forced back to the login screen.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sync.direct';

export interface RefreshResult {
  token: string;
  userId: string;
  username: string;
  role: string;
  expiresAt: string;
  mustChangePassword?: boolean;
}

/**
 * Exchange a stored (possibly expired) token for a fresh one.
 * Returns null when the session cannot be refreshed — banned, suspended, revoked, forged,
 * or expired longer ago than the server's refresh window. The caller must then sign in again.
 */
export async function refreshSession(token: string): Promise<RefreshResult | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data?.token) return null;
    return data as RefreshResult;
  } catch (e) {
    console.warn('[authApi] refresh failed:', e);
    return null;
  }
}
